import React, { useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { SOURCING_PRICE_IN_CENTS, SOURCING_DISCOUNT_PRICE_IN_CENTS } from '../../constants/pricings';
import { SourcingType } from '../../types';
import { ExpertsSourcingDrawerProps } from '.';

export const OPEN_DRAWER_EVENT_NAME = 'openExpertsSourcingDrawer';
export const WINDOW_INDICATION = 'isExpertsSourcingDrawerLoaded';

export const isExpertsSourcingDrawerMounted = () => !!window[WINDOW_INDICATION];

export const setIsExpertsSourcingDrawerMounted = (isOpen: boolean) => {
    window[WINDOW_INDICATION] = isOpen;
};

export interface LazyDrawerProps extends Omit<ExpertsSourcingDrawerProps, 'setIsOpen'> {
    setIsLoading?: (isOpen: boolean) => void;
    variant?: 'default' | 'sidebar';
    plansToOmit?: SourcingType[];
    customHeader?: React.ComponentType<{ onClick: () => void }>;
}

export const openExpertsSourcingDrawerInternal = ({
    currency,
    pageName,
    setIsLoading,
    onClose,
    variant = 'default',
    plansToOmit = [],
    customHeader,
    isPriceUpfrontEnabled,
    priceUpfrontConfiguration,
    priceUpfrontContext,
}: LazyDrawerProps) => {
    if (!isExpertsSourcingDrawerMounted()) {
        logger.error('Experts sourcing drawer is not mounted');
        return false;
    }

    const event = new CustomEvent<LazyDrawerProps>(OPEN_DRAWER_EVENT_NAME, {
        detail: {
            pageName,
            currency,
            isOpen: true,
            setIsLoading,
            onClose,
            variant,
            plansToOmit,
            customHeader,
            isPriceUpfrontEnabled,
            priceUpfrontConfiguration,
            priceUpfrontContext,
            sourcingPriceInCents: SOURCING_PRICE_IN_CENTS,
            sourcingDiscountPriceInCents: SOURCING_DISCOUNT_PRICE_IN_CENTS,
        },
    });

    window.dispatchEvent(event);
    return true;
};

export const useOpenExpertsSourcingDrawer = () => {
    const [isLoading, setIsLoading] = useState(false);

    const openExpertsSourcingDrawer = ({
        currency,
        pageName,
        onClose,
        variant = 'default',
        plansToOmit = [],
        customHeader,
        isPriceUpfrontEnabled,
        priceUpfrontConfiguration,
        priceUpfrontContext,
    }: Omit<LazyDrawerProps, 'sourcingPriceInCents' | 'sourcingDiscountPriceInCents'>) => {
        openExpertsSourcingDrawerInternal({
            currency,
            pageName,
            setIsLoading,
            onClose,
            variant,
            plansToOmit,
            customHeader,
            isPriceUpfrontEnabled,
            priceUpfrontConfiguration,
            priceUpfrontContext,
            sourcingPriceInCents: SOURCING_PRICE_IN_CENTS,
            sourcingDiscountPriceInCents: SOURCING_DISCOUNT_PRICE_IN_CENTS,
        });
    };

    return { openExpertsSourcingDrawer, isLoading };
};
